import { Calendar, Users, BarChart3, Clock, Home, UserPlus, LogOut, User, Heart, Mail, UsersIcon, Database } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useClinicData } from "@/hooks/useClinicData";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Sidebar as SidebarComponent,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
} from "@/components/ui/sidebar";

const menuItems = [
  {
    title: "Dashboard",
    url: "/",
    icon: Home,
  },
  {
    title: "Calendar",
    url: "/calendar",
    icon: Calendar,
    roles: ["admin", "doctor"] // Only show for admin and doctor roles
  },
  {
    title: "Patient Management",
    url: "/patients",
    icon: UsersIcon,
    roles: ["admin", "doctor", "staff"] // Show for admin, doctor, and staff roles
  },
  {
    title: "Patient Invitations",
    url: "/patient-invitations",
    icon: Mail,
    roles: ["admin", "doctor", "staff"] // Show for admin, doctor, and staff roles
  },
  {
    title: "Providers",
    url: "/providers",
    icon: Users,
    roles: ["admin", "doctor"] // Only show for admin and doctor roles
  }
];

export function Sidebar() {
  const location = useLocation();
  const { profile, logout } = useAuth();
  const { data: clinic } = useClinicData(profile?.clinic_id);

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => 
    !item.roles || item.roles.includes(profile?.role || "")
  );

  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile?.email || "User";
  };

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`;
    }
    return profile?.email?.[0]?.toUpperCase() || "U";
  };

  return (
    <SidebarComponent className="border-r border-gray-200">
      <SidebarHeader className="border-b border-gray-200 p-6">
        <div className="flex justify-between items-start w-full mb-3">
          <div className="flex flex-col pl-2">
            <div style={{ height: '1.75rem' }}>
              <img src="/clinicaflow-logo.svg" alt="CLINICAFLOW" className="h-full" />
            </div>
            <p className="text-sm text-gray-500 font-medium mt-0.5">Pro</p>
          </div>
          <div className="ml-4">
            <SidebarTrigger />
          </div>
        </div>
        
        {profile?.role === 'admin' && (
          <>
            <h2 className="mb-2 px-4 text-lg font-semibold tracking-tight">
              Admin
            </h2>
            <div className="space-y-1">
              {/* Admin-specific links here */}
            </div>
          </>
        )}
        
        
        {clinic && (
          <div className="mt-3 p-2 bg-blue-50 rounded-lg w-full">
            <p className="text-xs text-blue-600 font-medium">Connected to:</p>
            <p className="text-sm text-blue-800 font-semibold truncate">{clinic.name}</p>
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={location.pathname === item.url || (item.url === "/patients" && location.pathname.startsWith("/patients"))}
                    className="hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Link to={item.url}>
                      <item.icon className="w-5 h-5" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-gray-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={profile?.avatar_url || undefined} />
            <AvatarFallback>{getInitials()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{getDisplayName()}</p>
            <p className="text-xs text-gray-500 capitalize">{profile?.role || "user"}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={logout}
          className="w-full justify-start text-gray-600 hover:text-red-600 hover:bg-red-50"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </SidebarFooter>
    </SidebarComponent>
  );
}
